import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

interface InactivityWarningDialogProps {
  open: boolean;
  /** Epoch ms when the session will be terminated. */
  timeoutAt: number | null;
  /** Resets the inactivity clock and keeps the session alive. */
  onStaySignedIn: () => void;
  /** Signs the user out immediately. */
  onSignOut: () => void;
}

function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Warning shown 5 minutes before an inactive session is signed out (SOPHIA-34816).
 */
export function InactivityWarningDialog({
  open,
  timeoutAt,
  onStaySignedIn,
  onSignOut,
}: InactivityWarningDialogProps): React.ReactElement {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!open) return;
    setNow(Date.now());
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [open]);

  const remaining = timeoutAt != null ? timeoutAt - now : 0;

  return (
    <Dialog open={open} aria-labelledby="inactivity-warning-title" disableEscapeKeyDown>
      <DialogTitle id="inactivity-warning-title">Are you still there?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You&apos;ve been inactive for a while. For security, you will be signed out automatically
          in <strong>{formatRemaining(remaining)}</strong>.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSignOut} color="inherit">
          Sign out now
        </Button>
        <Button onClick={onStaySignedIn} variant="contained" autoFocus>
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
}
